import { useEffect, useRef, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ForgotFormData, LoginFormData } from '../components/Compounds/Modals/Login';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutUser } from './User/Logout';
import { PasswordFormData } from '../components/Compounds/Modals/ResetPassword';
import { resetPasswordApi, ResetPasswordResponse, userForgotPassword } from './User/passwordApi';
import { CallResult, sendKeptAlive } from './callExternalApi';
import { clearUserData, getCurrentPreferences, getSelectedAccountsSubject, updateUserSettings } from '../store/User';
import { datadogLogs } from '@datadog/browser-logs';
import { clearDataDogUser } from '../datadog';
import { useSnackbar } from '../Context/SnackbarContext';
import { addError, resetErrorStore } from '../store/Error';
import { clearNotificationStore } from '../store/Notifications';
import { redirectToQuery } from '../utils/helpers';
import { isUnprotectedRoute } from '../utils/unprotectedRoutes';
import { clearMentionableUpstackers } from '../store/MentionableUsers';
import { loginUser } from './User/Login';
import { getInventorySubject } from '../store/Inventory';
import { getCaseSubject } from '../store/SupportCase';
import { getWorkflowOrdersSubject } from '../store/WorkflowOrder';

export interface UseSessionReturn {
  fetchToken: (body: LoginFormData) => Promise<void>;
  tokenLoading: boolean;
  clearSession: (message?: string, tokenExpired?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (body: PasswordFormData) => Promise<CallResult<ResetPasswordResponse>>;
  forgotPassword: (body: ForgotFormData) => Promise<CallResult<void>>;
  getToken: () => string | undefined;
}

export interface Session {
  authenticated: boolean;
  token: string;
}

export const defaultSession = {
  authenticated: false,
  token: ''
};

const sessionSubject = new BehaviorSubject<Session>(defaultSession);
const emitErrorState = (message?: string): void => addError('session', message);

// A page that carries a redirectTo is only passing through to it; wrapped again, a token in it would escape scrubUrl.
const destinationOf = (pathname: string, search: string): string =>
  new URLSearchParams(search).get('redirectTo') || `${pathname}${search}`;

const clearSignedInStores = () => {
  clearUserData();
  clearDataDogUser();
  clearNotificationStore();
  clearMentionableUpstackers();
  getInventorySubject().next([]);
  getCaseSubject().next([]);
  getWorkflowOrdersSubject().next([]);
  getSelectedAccountsSubject().next([]);
};

// A failed sign-out leaves the token valid on the server; 404 is the answer for one that is already gone.
const endServerSession = async () => {
  const { error } = await logoutUser();
  const status = error?.errorResponse?.response?.status;
  if (error && status !== 404) {
    datadogLogs.logger.error('Sign-out did not end the session on the server', { status, message: error.message });
  }
};

export const getSessionSubject = (): BehaviorSubject<Session> => sessionSubject;
export const updateSession = (update: Partial<Session>) => sessionSubject.next({ ...sessionSubject.value, ...update });
export const getSession = () => sessionSubject.value;

export const useSession = (): UseSessionReturn => {
  const { setSnack } = useSnackbar();

  const [tokenLoading, setTokenLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Callers hold on to clearSession past the render that created it, so the redirect target must not come from a closure.
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const fetchToken = async (body: LoginFormData) => {
    setTokenLoading(true);
    emitErrorState();

    const result = await loginUser(body);

    if (result.otpRequired) {
      const redirectTo = new URLSearchParams(location.search).get('redirectTo') ?? undefined;

      // Router state rather than the query string: analytics record every URL the app navigates to.
      navigate('/mfa', { state: { otpSessionToken: result.otpSessionToken, redirectTo } });
      setTokenLoading(false);
      return;
    }

    const { data, headers, error } = result;

    if (data && headers) {
      const { authorization } = headers;
      const token = authorization.replace('Bearer ', '');

      // Store session in local storage.
      localStorage.setItem('token', token);

      updateSession({ authenticated: true, token });
      resetErrorStore();
    }

    if (error) {
      emitErrorState(error?.message);
      datadogLogs.logger.error('Error when fetching token: ', error);
    }

    setTokenLoading(false);
  };

  const getToken = () => {
    const token = localStorage.getItem('token');

    if (!token) return;

    updateSession({ token, authenticated: true });

    return token;
  };

  const clearSession = async (message?: string, tokenExpired = false, keepDestination = true) => {
    // Read before the session is cleared: a route guard answers that by navigating to /login,
    // and saving preferences is awaited, so by the end /login is all this would have left to record.
    const { pathname, search } = locationRef.current;
    const token = localStorage.getItem('token');
    resetErrorStore();
    updateSession(defaultSession);

    if (token) {
      if (!tokenExpired) {
        // Store user prefs if token is still valid
        const currentPreferences = getCurrentPreferences();
        if (currentPreferences.id) await updateUserSettings(currentPreferences);
      }
      // Sent while the token is still stored and kept alive past a closing tab; unawaited, so the redirect never waits.
      sendKeptAlive(() => void endServerSession());
      localStorage.removeItem('token');
    }
    // Left by earlier versions of the portal, which opened Action Cable with it.
    localStorage.removeItem('action-cable-credentials');

    clearSignedInStores();

    if (!keepDestination) navigate('/login');
    else if (!isUnprotectedRoute(pathname)) navigate(`/login?${redirectToQuery(destinationOf(pathname, search))}`);
    if (message) setSnack({ message, type: 'error', open: true });
  };

  // Check local storage for existing session on init.
  useEffect(() => {
    getToken();
  }, []);

  const resetPassword = async (body: PasswordFormData): Promise<CallResult<ResetPasswordResponse>> => {
    setTokenLoading(true);

    const res = await resetPasswordApi(body);

    setTokenLoading(false);

    return res;
  };

  const forgotPassword = async (body: ForgotFormData): Promise<CallResult<void>> => {
    setTokenLoading(true);

    const res = await userForgotPassword(body);
    if (res.error) emitErrorState(res.error.message);

    setTokenLoading(false);

    return res;
  };

  // A session the user ended on purpose must not hand the next sign-in the page they left.
  const signOut = () => clearSession(undefined, false, false);

  return { fetchToken, tokenLoading, clearSession, signOut, getToken, resetPassword, forgotPassword };
};
